import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { ConfiguratorContext } from "../../context";
import useKintoOnePersonalData from "../../hooks/useKintoOnePersonalData";
import useKintoSimulationData, { nextKintoRequestId } from "../../hooks/useKintoSimulationData";
import usePersonalData from "../../hooks/usePersonalData";
import InputOptions from "../../../utils/InputOptions";
import Axios from "axios";
import { CurrencyFormat } from "../../../utils";

// Configuración de CSRF para requests
Axios.defaults.headers.common["X-CSRFTOKEN"] = document.querySelector('meta[name="csrf-token"]').content;

/**
 * Simulador de Kinto (alquiler mensual) para v2
 * Permite configurar pie, tiempo y kilometraje para simular cuotas
 * Auto-simula con valores por defecto al montarse si no hay resultado previo
 */
const KintoSimulation = ({ onResultChange, isHidden = false, autoSimulateOnMount = true }) => {
    // Verificación de disponibilidad del contexto
    if (!ConfiguratorContext) {
        console.error(
            "❌ [KintoSimulation] ConfiguratorContext not found. Make sure the component is wrapped with ConfiguratorProvider."
        );
        return <div>Error: ConfiguratorContext not available</div>;
    }

    const context = useContext(ConfiguratorContext);

    if (!context) {
        console.error(
            "❌ [KintoSimulation] Context not found. Make sure the component is wrapped with ConfiguratorProvider."
        );
        return <div>Error: Context not available</div>;
    }

    const [state] = context;
    const { selectedVersion } = state || {};

    // Hooks para manejo de datos de simulación Kinto
    const {
        kintoData: kintoSimData,
        updateDownpayment,
        updateTime,
        updateKilometers,
        applyKintoResult,
        setIsLoading,
        clearError,
    } = useKintoSimulationData();

    // Empresa simula KINTO One Business (product 2); persona natural,
    // One Personal (product 1)
    const { isCompany } = usePersonalData();
    const kintoProduct = isCompany() ? 2 : 1;

    // Hook para datos personales y configuración específica de Kinto
    const kintoHook = useKintoOnePersonalData() || {};
    const {
        setKintoOnePersonalData = () => {},
        updateKintoConfiguration = () => {},
        getAvailableOptions = () => ({}),
        getCurrentKintoData = () => ({}),
        setKintoApiResponse = () => {},
        getKintoApiResponse = () => null,
    } = kintoHook;

    // Datos actuales de la configuración Kinto
    const kintoData = getCurrentKintoData() || {};
    const availableOptions = getAvailableOptions() || {};

    // Estados locales para simulación
    const [simulationData, setSimulationData] = useState({
        downpayment: kintoSimData.downpayment,
        time: kintoSimData.time,
        kilometers: kintoSimData.kilometers,
    });
    const [showResult, setShowResult] = useState(kintoSimData.hasResult);
    const [showTerms, setShowTerms] = useState(false);

    const apiResponse = getKintoApiResponse();
    const initializedVersionRef = useRef(null);
    const [hasAutoSimulated, setHasAutoSimulated] = useState(false);

    // Sincronizar datos locales con el estado global de Kinto
    useEffect(() => {
        setSimulationData({
            downpayment: kintoSimData.downpayment,
            time: kintoSimData.time,
            kilometers: kintoSimData.kilometers,
        });
        // Mostrar resultado si hay resultado exitoso O si hay un error
        setShowResult(kintoSimData.hasResult || !!kintoSimData.error);
    }, [kintoSimData]);

    // Inicializa los datos de Kinto cuando cambia la versión
    const initializeKintoData = useCallback(
        (version) => {
            if (version?.kinto_car && initializedVersionRef.current !== version.id) {
                setKintoOnePersonalData(version.kinto_car);
                initializedVersionRef.current = version.id;
            }
        },
        [setKintoOnePersonalData]
    );

    useEffect(() => {
        initializeKintoData(selectedVersion);
    }, [selectedVersion?.id, initializeKintoData]);

    // Auto-simulación al montar el componente si no hay resultado previo
    useEffect(() => {
        // Solo auto-simular si:
        // 1. autoSimulateOnMount está habilitado
        // 2. No se ha auto-simulado antes
        // 3. No hay resultado previo
        // 4. El componente no está oculto
        // 5. Los datos de Kinto están inicializados
        if (
            autoSimulateOnMount &&
            !hasAutoSimulated &&
            !kintoSimData.hasResult &&
            !isHidden &&
            kintoData?.car_data?.id
        ) {
            // console.log("Kinto: Auto-simulando con valores por defecto");
            setHasAutoSimulated(true);

            // Ejecutar simulación con valores por defecto después de un pequeño delay
            // para asegurar que todos los datos necesarios estén disponibles
            setTimeout(() => {
                handleSimulation(true);
            }, 500);
        }
    }, [autoSimulateOnMount, hasAutoSimulated, kintoSimData.hasResult, isHidden, kintoData?.car_data?.id]);

    useEffect(() => {
        if (onResultChange) {
            onResultChange(showResult);
        }
    }, [showResult, onResultChange]);

    const handleSimulation = async (isAutoSimulation = false) => {
        const kintoCarId = kintoData?.car_data?.id;

        if (!kintoCarId) {
            console.warn("⚠️ [KintoSimulation] No Kinto Car ID available for simulation");
            return;
        }

        // Identificación de la petición: permite descartar la respuesta si el
        // usuario cambia de versión mientras la simulación está en vuelo
        const versionId = selectedVersion?.id;
        const requestId = nextKintoRequestId();

        setIsLoading(true);
        clearError(); // Limpiar errores previos

        // Solo enviar evento de dataLayer si es una simulación manual (no automática)
        if (!isAutoSimulation && dataLayer) {
            dataLayer.push({
                'event': 'cotizador',
                'eventCategory': 'opciones_de_pago',
                'eventAction': 'cta',
                'eventLabel': 'simula_kinto'
            });
        }

        if (updateKintoConfiguration) {
            updateKintoConfiguration(
                simulationData.time,
                simulationData.downpayment,
                simulationData.kilometers
            );
        }

        const apiData = {
            car: kintoCarId,
            time: simulationData.time,
            distance: simulationData.kilometers,
            downpayment: String(simulationData.downpayment),
            product: kintoProduct,
        };

        try {
            const axiosInstance = Axios.create({
                baseURL: "",
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "Content-Type": "application/json",
                },
            });

            const response = await axiosInstance.post("/api/kinto/service_simulation/", apiData);

            // Verificar si la respuesta contiene datos válidos
            if (response.data && response.data.amortization && response.data.amortization.length > 0) {
                setKintoApiResponse(response.data, versionId);
                applyKintoResult(versionId, requestId, {
                    hasResult: true,
                    resultData: response.data,
                    error: null,
                    // Configuración que respalda esta cuota
                    simulated_for: {
                        version_id: versionId,
                        downpayment: simulationData.downpayment,
                        time: simulationData.time,
                        kilometers: simulationData.kilometers,
                    },
                });
                setShowResult(true);
            } else {
                // Si no hay datos de amortización, mostrar error
                const errorMsg = "Esta combinación de tiempo y kilometraje no está disponible. Por favor, inténtalo nuevamente.";
                console.warn("[Kinto] Sin datos de amortización en la respuesta");
                
                if (!isAutoSimulation) {
                    applyKintoResult(versionId, requestId, {
                        error: errorMsg,
                        hasResult: false,
                        resultData: null,
                        simulated_for: null,
                        isLoading: false,
                    });
                    setShowResult(true);
                }
            }
        } catch (error) {
            console.error("[Kinto] Error en la simulación:", error.message);

            const errorMessage = error.response?.data?.error
                || error.response?.data?.message
                || "Error al realizar la simulación. Por favor, intenta con otra combinación.";

            applyKintoResult(versionId, requestId, {
                error: errorMessage,
                hasResult: false,
                resultData: null,
                simulated_for: null,
                isLoading: false,
            });
            setShowResult(true);
        } finally {
            // También pasa por la guarda: una respuesta obsoleta no debe apagar
            // el loading de una simulación posterior que sigue en curso
            applyKintoResult(versionId, requestId, { isLoading: false });
        }
    };

    const handleChangeDownpayment = (value) => {
        const newData = { ...simulationData, downpayment: value };
        setSimulationData(newData);
        updateDownpayment(value);
        if (kintoSimData.error) {
            clearError();
            setShowResult(false);
        }
    };

    const handleChangeTime = (value) => {
        const newData = { ...simulationData, time: value };
        setSimulationData(newData);
        updateTime(value);
        if (kintoSimData.error) {
            clearError();
            setShowResult(false);
        }
    };

    const handleChangeKms = (value) => {
        const newData = { ...simulationData, kilometers: value };
        setSimulationData(newData);
        updateKilometers(value);
        if (kintoSimData.error) {
            clearError();
            setShowResult(false);
        }
    };

    const toggleTerms = () => {
        setShowTerms((prev) => !prev);
    };

    return (
        <div className={`kinto-simulation ${isHidden ? "hidden" : ""}`} id="kinto-simulation">
            <div className="component-body">
                <div className="mini-simulator">
                    {!showResult ? (
                        <>
                            <div className="inputs-container">
                                {/* <InputOptions
                                    onChangeValue={handleChangeDownpayment}
                                    title="Porcentaje de Pie"
                                    subtitle="Hasta un 20% del valor total del vehículo."
                                    options={
                                        availableOptions.downpayments?.length > 0
                                            ? availableOptions.downpayments.map((value) => ({
                                                  value,
                                                  label: `${value}%`,
                                              }))
                                            : [
                                                  { value: 0, label: "0%" },
                                                  { value: 20, label: "20%" },
                                              ]
                                    }
                                    defaultValue={simulationData.downpayment}
                                /> */}
                                <InputOptions
                                    onChangeValue={handleChangeTime}
                                    className="time"
                                    title="¿Cuánto tiempo lo quieres?"
                                    subtitle="Seleccione el periodo de renta del vehículo."
                                    options={
                                        availableOptions.months?.length > 0
                                            ? availableOptions.months.map((value) => ({
                                                  value,
                                                  label: `${value} meses`,
                                              }))
                                            : [
                                                  { value: 12, label: "12 meses" },
                                                  { value: 24, label: "24 meses" },
                                                  { value: 36, label: "36 meses" },
                                                  { value: 48, label: "48 meses" },
                                              ]
                                    }
                                    defaultValue={simulationData.time}
                                />
                                <InputOptions
                                    onChangeValue={handleChangeKms}
                                    title="¿Cuántos kilómetros usarás al año?"
                                    subtitle="Seleccione el límite de kilómetros de uso del vehículo."
                                    options={
                                        availableOptions.kilometers?.length > 0
                                            ? availableOptions.kilometers.map((value) => ({
                                                  value,
                                                  label: `${value.toLocaleString()} Km`,
                                              }))
                                            : [
                                                  { value: 10000, label: "10.000 Km" },
                                                  { value: 12000, label: "12.000 Km" },
                                                  { value: 15000, label: "15.000 Km" },
                                                  { value: 18000, label: "18.000 Km" },
                                                  { value: 20000, label: "20.000 Km" },
                                                  { value: 24000, label: "24.000 Km" },
                                              ]
                                    }
                                    defaultValue={simulationData.kilometers}
                                />
                            </div>
                            <div className="button-container">
                                {kintoSimData.isLoading ? (
                                    <div className="loading-spinner">
                                        <div className="spinner"></div>
                                    </div>
                                ) : (
                                    <button
                                        className="btn outline-black"
                                        onClick={() => handleSimulation(false)}
                                        type="button"
                                        disabled={kintoSimData.isLoading}
                                    >
                                        Simula tu cuota mensual
                                    </button>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="simulation-result">
                            {kintoSimData.error ? (
                                // Mostrar mensaje de error
                                <div className="error-message">
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px' }}>
                                        <circle cx="24" cy="24" r="22" stroke="#c53030" strokeWidth="2" fill="none"/>
                                        <path d="M24 14V26" stroke="#c53030" strokeWidth="2" strokeLinecap="round"/>
                                        <circle cx="24" cy="32" r="1.5" fill="#c53030"/>
                                    </svg>
                                    <p className="error-title">No se pudo realizar la simulación</p>
                                    <p className="error-text">{kintoSimData.error}</p>
                                    <button
                                        className="btn outline-black retry-button"
                                        onClick={() => {
                                            setShowResult(false);
                                            clearError();
                                        }}
                                        type="button"
                                    >
                                        Volver a simular
                                    </button>
                                </div>
                            ) : (
                                // Mostrar resultado exitoso
                                <>
                                    <p className="title">
                                        {/* {simulationData.downpayment}% Pie |*/} {simulationData.time} Meses |{" "}
                                        {simulationData.kilometers.toLocaleString()} Km
                                    </p>
                                    <p className="title">Todo incluido</p>
                                    <p className="price">
                                        {apiResponse?.amortization && apiResponse.amortization.length > 0
                                            ? CurrencyFormat(
                                                  Number(
                                                      apiResponse.amortization[
                                                          apiResponse.amortization.length - 1
                                                      ].totalQuota
                                                  )
                                              )
                                            : "-"}
                                    </p>
                                    <p className="iva">(IVA incluido)</p>
                                    <div className="terms" onClick={toggleTerms}>
                                        <div className="visible-part">
                                            <p>Ver términos y condiciones</p>
                                            <svg
                                                width="14"
                                                height="9"
                                                viewBox="0 0 14 9"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M13.4766 7.3605C13.4752 7.8226 13.2098 8.23746 12.8045 8.41358C12.3991 8.58934 11.9325 8.49194 11.6211 8.16752L6.94704 3.25839L2.27295 8.16752C1.84183 8.55638 1.19688 8.53075 0.795711 8.10747C0.393144 7.68529 0.368419 7.00752 0.738599 6.55385L6.17987 0.832566C6.60437 0.389145 7.28971 0.389145 7.71422 0.832566L13.1555 6.55384C13.3596 6.76695 13.4752 7.05732 13.4766 7.3605Z"
                                                    fill="#191919"
                                                />
                                            </svg>
                                        </div>
                                        <div className={"hidden-part" + (showTerms ? " active" : "")}>
                                            <p>
                                                El monto de la cuota de leasing es meramente referencial no
                                                vinculante los montos en pesos chilenos informados y demas
                                                condiciones de la operacion de leasing están sujetas a
                                                variación de la fecha de contratación, plazo. No se
                                                considera mes de gracia o pago diferido. Los valores
                                                informados incluyen: gastos propios del leasing, (SOAP,
                                                permisos de circulación, inscripción, impuesto verde,
                                                mantenciones, gastos administrativos, GPS).
                                            </p>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KintoSimulation;
